import styled from 'styled-components'
import { ReadMore } from '../ReadMore/ReadMore'
import { ResponsiveImage } from '../ResponsiveImage/ResponsiveImage'

export const Card = styled.section`
  width: 100%;
  padding: 1.2rem 2rem 3rem 2rem;
  background-color: ${({ theme }) => theme.colors.boxBackground};
  font-family: ${({ theme }) => theme.fonts.brand};
  font-weight: 300;

  @media screen and (min-width: 768px) {
    padding: 3rem 2rem;
  }

  b,
  strong {
    font-weight: 500;
  }

  p {
    margin: 0 0 1rem;
    padding: 0;
    font-size: 1.7rem;
    line-height: 155%;
    font-family: ${({ theme }) => theme.fonts.brand};
    font-weight: 300;

    a,
    span {
      font: inherit;
      text-decoration: underline;
      color: ${({ theme }) => theme.colors.primary};
    }
  }

  ol {
    margin-top: 1rem;
    margin-left: -1rem;
    counter-reset: custom-counter;
    list-style: none;
    padding-left: 40px;
    font-size: 1.7rem;

    li {
      line-height: 1.6;
      margin: 0 0 2.5rem 0;
      counter-increment: custom-counter;
      position: relative;

      &:before {
        content: counter(custom-counter) '.';
        margin-top: 0.2rem;
        font-size: 1.4rem;
        position: absolute;
        --size: 2rem;
        left: calc(-1 * var(--size) - 10px);
        line-height: var(--size);
        width: var(--size);
        height: var(--size);
        top: 0;
        text-align: center;
      }
    }
  }
`

export const Img = styled(ResponsiveImage)`
  aspect-ratio: 1;
  object-fit: cover;
  width: 80%;
  /* Center the image within the wrapper so overlay center aligns with image center */
  display: block;
  margin: 0 auto;

  @supports not (aspect-ratio: 1) {
    &::before {
      float: left;
      padding-top: 100%;
      content: '';
    }

    &::after {
      display: block;
      content: '';
      clear: both;
    }
  }

  @media screen and (min-width: 768px) {
    width: 100%;
  }
`

export const WrapperText = styled.div<{
  cardHasImages: boolean
}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;

  @media screen and (min-width: 768px) {
    flex: 45%;
    max-height: 43rem;
    overflow-y: auto;
    scroll-behavior: smooth;
  }
`

export const WrapperImg = styled.div`
  position: relative;

  @media screen and (min-width: 768px) {
    flex: 55%;
    overflow: hidden;
  }
`

export const WrapperImages = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  margin-top: 3.7rem;
  margin-bottom: 1.7rem;

  @media screen and (min-width: 768px) {
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: flex-start;
    width: calc(62.05% - 4rem);
    aspect-ratio: 0.93;
    margin-top: 0;
    margin-bottom: 0;
  }
`

export const SignatureWrapper = styled.div`
  align-self: flex-end;
  width: 54.87%;
  margin-top: 1.6rem;
  margin-right: 2rem;

  @media screen and (min-width: 768px) {
    width: 77.26%;
    margin-top: 3.6rem;
    margin-right: 0;
  }
`

export const SignatureImage = styled(ResponsiveImage)`
  width: 100%;
  aspect-ratio: 4;
  object-fit: contain;

  @supports not (aspect-ratio: 4) {
    &::before {
      float: left;
      padding-top: 25%;
      content: '';
    }

    &::after {
      display: block;
      content: '';
      clear: both;
    }
  }
`

export const BorderedImageWrapper = styled.div`
  width: 43.73%;
  margin-right: -3.6rem;
  box-shadow: 0.8rem 0.9rem 0 ${({ theme }) => theme.colors.primary};
  z-index: 1;

  @media screen and (min-width: 768px) {
    width: 42%;
    margin-top: 3.5rem;
    margin-right: 0;
    margin-bottom: -2rem;
    z-index: 30;
  }
`

export const BorderedImage = styled(ResponsiveImage)`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;

  @supports not (aspect-ratio: 1) {
    &::before {
      float: left;
      padding-top: 100%;
      content: '';
    }

    &::after {
      display: block;
      content: '';
      clear: both;
    }
  }
`

export const ImageBlockWrapper = styled.div`
  width: 45.52%;
  margin-top: 3.4rem;

  @media screen and (min-width: 768px) {
    width: 43.73%;
    margin-top: 0;
    margin-left: 4.9rem;
    margin-right: -3.2rem;
    z-index: 20;
  }
`

export const ImageBlock = styled(ResponsiveImage)`
  width: 100%;
  aspect-ratio: 0.73;
  object-fit: cover;

  @supports not (aspect-ratio: 0.73) {
    &::before {
      float: left;
      padding-top: 137.64%;
      content: '';
    }

    &::after {
      display: block;
      content: '';
      clear: both;
    }
  }
`

export const WrapIcon = styled.div`
  display: flex;
  width: 60%;
  height: 2rem;
  margin: 1rem 0;

  svg {
    width: 20px;
    height: 20px;
    margin-right: 4px;
    stroke-opacity: 0;
  }

  @media screen and (min-width: 768px) {
    width: 50%;
  }
`

export const StyledReadMore = styled(ReadMore)`
  justify-content: flex-end;

  @media screen and (min-width: 768px) {
    justify-content: flex-start;
  }
`

export const InstructionsCollapse = styled(StyledReadMore)`
  padding: 1.5rem 0;
`

export const Content = styled.div<{ hasImage: boolean }>`
  position: relative;
  display: flex;
  gap: 3rem;
  flex-direction: column;
  width: 100%;
  min-width: 20rem;

  @media screen and (min-width: 768px) {
    flex-direction: row-reverse;
    align-items: center;
    gap: 4rem;
    justify-content: space-between;
  }
`

export const InstructionImages = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-top: 3rem;
  gap: 1rem;
  margin-top: 8rem;

  @media screen and (min-width: 768px) {
    margin-top: 2rem;
  }
`

export const Player = styled.video`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`

export const ClickArea = styled.button`
  width: 100%;
  /* Match the image container height to ensure proper vertical centering */
  height: 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: transparent;
  padding: 0;
  border: 0 none;
  color: ${({ theme }) => theme.colors.background};
  z-index: 3;
  display: flex;
  justify-content: center;
  align-items: center;
`
